import { emit, listen } from "@tauri-apps/api/event";
import { openUrl } from "@tauri-apps/plugin-opener";

export interface BrowserRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Open an external URL in the user's real browser. In the Tauri WebView,
 *  window.open returns null, so route through the OS opener; on the web,
 *  use a sandboxed new tab. */
export async function openExternal(url: string): Promise<void> {
  if (isTauri()) {
    await openUrl(url);
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

async function emitBrowserEvent<T>(event: string, payload?: T): Promise<void> {
  if (!isTauri()) return;
  try {
    await emit(event, payload ?? {});
  } catch {
    // Best-effort: these are fire-and-forget control events for the native
    // browser-pane host. The host may not be ready yet (or absent in test
    // envs that stub __TAURI_INTERNALS__ without the event channel). A failed
    // emit must never surface as an unhandled promise rejection.
  }
}

export async function openNative(rect: BrowserRect, url: string): Promise<void> {
  await emitBrowserEvent("browserpane:open", { rect, url });
}

export async function setNativeBounds(rect: BrowserRect): Promise<void> {
  await emitBrowserEvent("browserpane:set_bounds", { rect });
}

export async function navigateNative(url: string): Promise<void> {
  await emitBrowserEvent("browserpane:navigate", { url });
}

export async function backNative(): Promise<void> {
  await emitBrowserEvent("browserpane:back");
}

export async function forwardNative(): Promise<void> {
  await emitBrowserEvent("browserpane:forward");
}

export async function reloadNative(): Promise<void> {
  await emitBrowserEvent("browserpane:reload");
}

export async function showNative(): Promise<void> {
  await emitBrowserEvent("browserpane:show");
}

export async function hideNative(): Promise<void> {
  await emitBrowserEvent("browserpane:hide");
}

export async function closeNative(): Promise<void> {
  await emitBrowserEvent("browserpane:close");
}

export async function onNativeNavigated(callback: (url: string) => void): Promise<() => void> {
  if (!isTauri()) return () => {};
  try {
    const unlisten = await listen<{ url?: string }>("browser:navigated", (event) => {
      const url = event.payload?.url;
      if (url) callback(url);
    });
    return unlisten;
  } catch {
    // Same robustness as emitBrowserEvent: a failed subscribe must not reject
    // unhandled. Return a no-op disposer so callers' cleanup stays valid.
    return () => {};
  }
}
